import { Injectable, Inject } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { timeout, retry, catchError } from 'rxjs/operators';
import { of, firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class DatadogService {
    private apiUrl = `${environment.apiHost}/location`;

    constructor(private http: HttpClient, @Inject(OKTA_AUTH) public oktaAuth: OktaAuth) {
    }

    /**
     * POST rather than GET so neither the browser nor the API Gateway stage cache
     * (300s TTL, keyed only on path + storeNumber) can hand the same single-use
     * signed URL to a second caller, which Datadog rejects as a replay.
     */
    async getSecureEmbedUrl(storeNumber: string) {
        return this.http.post(`${this.apiUrl}/datadog-embed-url?storeNumber=${storeNumber}`, null,
            {
                headers: new HttpHeaders(
                    { "Authorization": "Bearer " + await this.oktaAuth.getAccessToken() }
                )
            }).pipe(
                timeout(10000),
                retry(2),
                catchError(error => of(null))
            );
    }

    /**
     * Resolves to a freshly signed, single-use Datadog embed URL, or null when one
     * could not be minted. Each URL expires 30 minutes after it is signed and is
     * consumed on first load, so callers must request a new one per iframe load.
     */
    async getSecureEmbedUrlForStore(storeNumber: string): Promise<string> {
        try {
            const observable = await this.getSecureEmbedUrl(storeNumber);
            const response = await firstValueFrom(observable);
            if (!response || response["status"] !== 200) {
                return null;
            }
            return response["message"]["iframeUrl"] || null;
        } catch (error) {
            console.log("Request for Datadog embed URL failed!", error);
            return null;
        }
    }
}
